import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import {
  ChevronLeft, ChevronRight, MapPin, Maximize2, Bath, Layers, Calendar,
  Car, Droplets, Trees, FileText, Navigation, Heart, Phone, Send,
  ArrowRight, TrendingUp, DollarSign, Award, Shield, Building2,
  Video, Eye as EyeIcon, Percent, Home, Sofa, Mountain, Sunrise,
  Fence, ArrowUpRight, Play, Download, RotateCcw
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { isRTL } from "@/i18n";
import { useCurrency } from "@/contexts/CurrencyContext";
import { properties as staticProperties, propertyDescriptions } from "@/data/properties";
import { useProperty } from "@/hooks/useProperties";
import { transformProperty, incrementContacts } from "@/services/propertyService";
import { submitContactForm } from "@/services/contactService";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";

const BASE_URL = "https://olyinvestment.com";

/**
 * JSON-LD Structured Data (RealEstateListing + Product)
 */
function generateStructuredData(property, title, desc, location, lang, imageUrl) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    name: title,
    description: desc?.substring(0, 500) || "",
    url: `${BASE_URL}/${lang}/properties/${property.slug || property.id}`,
    datePosted: property.created_at,
    image: imageUrl || property.image,
    offers: {
      "@type": "Offer",
      price: property.priceGBP,
      priceCurrency: "GBP",
      availability: "https://schema.org/InStock",
      url: `${BASE_URL}/${lang}/properties/${property.slug || property.id}`,
    },
    address: {
      "@type": "PostalAddress",
      addressLocality: location,
      addressRegion: "North Cyprus",
      addressCountry: "CY",
    },
  };

  if (property.lat && property.lng) {
    schema.geo = {
      "@type": "GeoCoordinates",
      latitude: property.lat,
      longitude: property.lng,
    };
  }

  if (property.sqm) {
    schema.floorSize = {
      "@type": "QuantitativeValue",
      value: property.sqm,
      unitCode: "MTK",
    };
  }

  if (property.bedrooms > 0) {
    schema.numberOfRooms = property.bedrooms;
  }

  if (property.bathrooms > 0) {
    schema.numberOfBathroomsTotal = property.bathrooms;
  }

  if (property.yearBuilt > 0) {
    schema.yearBuilt = property.yearBuilt;
  }

  // Organization
  schema.seller = {
    "@type": "RealEstateAgent",
    name: "OlyInvestment",
    url: BASE_URL,
    telephone: "+905338000000",
    address: {
      "@type": "PostalAddress",
      addressCountry: "CY",
      addressRegion: "North Cyprus",
    },
  };

  return schema;
}

/**
 * BreadcrumbList JSON-LD
 */
function generateBreadcrumbSchema(lang, title) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: `${BASE_URL}/${lang}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Properties",
        item: `${BASE_URL}/${lang}#listings`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: title,
      },
    ],
  };
}

export default function PropertyDetailPage() {
  const { lang, id } = useParams();
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const rtl = isRTL(i18n.language);
  const [currentImg, setCurrentImg] = useState(0);
  const [formSent, setFormSent] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });

  // Supabase'den veriyi çek
  const { property: rawProperty, loading, error } = useProperty(id);

  // Veriyi transform et veya fallback kullan
  const property = useMemo(() => {
    if (rawProperty && !error) {
      try {
        return transformProperty(rawProperty, i18n.language);
      } catch (err) {
        console.error('Error transforming property:', err);
        return staticProperties.find((p) => String(p.id) === String(id));
      }
    }
    return staticProperties.find((p) => String(p.id) === String(id));
  }, [rawProperty, error, i18n.language, id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-24 pb-20 max-w-7xl mx-auto px-6 md:px-12">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-gray-200 rounded w-1/3" />
            <div className="h-96 bg-gray-200 rounded" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-4">
                <div className="h-6 bg-gray-200 rounded w-3/4" />
                <div className="h-4 bg-gray-200 rounded w-full" />
                <div className="h-4 bg-gray-200 rounded w-5/6" />
              </div>
              <div className="h-64 bg-gray-200 rounded" />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center text-xl">
        {t("property_detail.not_found") || "Property not found"}
      </div>
    );
  }

  // Supabase'den mi geldi yoksa statik mi
  const isFromSupabase = !property.titleKey;
  const title = isFromSupabase ? property.title : t(`listings.properties.${property.titleKey}.title`);
  const location = isFromSupabase ? property.location : t(`listings.properties.${property.titleKey}.location`);
  const desc = isFromSupabase ? property.description : (propertyDescriptions[i18n.language]?.[property.descKey] || propertyDescriptions.en[property.descKey]);
  const metaTitle = property.metaTitle || `${title} | OlyInvestment`;
  const metaDescription = property.metaDescription || desc?.substring(0, 160) || "";
  const metaKeywords = property.metaKeywords || "";
  const canonicalUrl = `${BASE_URL}/${lang}/properties/${property.slug || property.id}`;
  const primaryImage = property.images?.[0] || property.image || "";

  const similar = staticProperties.filter((p) => String(p.id) !== String(property.id) && p.type === property.type).slice(0, 3);

  const features = [
    { icon: Maximize2, label: t("property_detail.area"), value: typeof property.sqm === 'number' ? `${property.sqm} m²` : property.sqm },
    ...(property.bedrooms > 0 ? [{ icon: Layers, label: t("property_detail.rooms"), value: property.bedrooms }] : []),
    ...(property.bathrooms > 0 ? [{ icon: Bath, label: t("property_detail.bathrooms"), value: property.bathrooms }] : []),
    ...(property.distanceSea ? [{ icon: Navigation, label: t("property_detail.distance_sea"), value: property.distanceSea }] : []),
    ...(property.titleType ? [{ icon: FileText, label: t("property_detail.title_type"), value: t(`property_detail.${property.titleType}`) }] : []),
    ...(property.yearBuilt > 0 ? [{ icon: Calendar, label: t("property_detail.year_built"), value: property.yearBuilt }] : []),
    { icon: Car, label: t("property_detail.parking"), value: property.parking ? t("property_detail.yes") : t("property_detail.no") },
    { icon: Droplets, label: t("property_detail.pool"), value: property.pool ? t("property_detail.yes") : t("property_detail.no") },
    { icon: Trees, label: t("property_detail.garden"), value: property.garden ? t("property_detail.yes") : t("property_detail.no") },
  ];

  // Ek olanaklar listesi
  const amenities = [
    ...(property.furnished ? [{ label: "Mobilyalı", icon: "🛋️" }] : []),
    ...(property.seaView ? [{ label: "Deniz Manzarası", icon: "🌊" }] : []),
    ...(property.mountainView ? [{ label: "Dağ Manzarası", icon: "⛰️" }] : []),
    ...(property.cityView ? [{ label: "Şehir Manzarası", icon: "🏙️" }] : []),
    ...(property.balcony ? [{ label: "Balkon", icon: "🪟" }] : []),
    ...(property.terrace ? [{ label: "Teras", icon: "🏡" }] : []),
    ...(property.elevator ? [{ label: "Asansör", icon: "🛗" }] : []),
    ...(property.security247 ? [{ label: "24/7 Güvenlik", icon: "🔒" }] : []),
    ...(property.gym ? [{ label: "Spor Salonu", icon: "🏋️" }] : []),
    ...(property.spa ? [{ label: "SPA", icon: "💆" }] : []),
    ...(property.playground ? [{ label: "Oyun Alanı", icon: "🎠" }] : []),
    ...(property.concierge ? [{ label: "Concierge", icon: "🎩" }] : []),
    ...(property.solarPanels ? [{ label: "Solar Panel", icon: "☀️" }] : []),
    ...(property.smartHome ? [{ label: "Akıllı Ev", icon: "🏠" }] : []),
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      await submitContactForm({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message || `${t("property_detail.inquiry_about")} ${title}`,
        propertyId: property.id,
        inquiryType: 'property',
        language: i18n.language
      });

      // İletişim sayacını artır
      if (property.id) {
        incrementContacts(property.id).catch(() => {});
      }

      setFormSent(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setFormSent(false), 3000);
    } catch (err) {
      console.error('Failed to submit contact form:', err);
      alert(t("property_detail.form_error") || "Failed to send message. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Proje durumu çeviri
  const projectStatusMap = {
    planning: { tr: "Planlama Aşamasında", en: "Planning Phase" },
    under_construction: { tr: "İnşaat Devam Ediyor", en: "Under Construction" },
    completed: { tr: "Tamamlandı", en: "Completed" },
    ready_to_move: { tr: "Taşınmaya Hazır", en: "Ready to Move" },
  };

  const appreciationMap = {
    low: { tr: "Düşük", en: "Low" },
    medium: { tr: "Orta", en: "Medium" },
    high: { tr: "Yüksek", en: "High" },
    very_high: { tr: "Çok Yüksek", en: "Very High" },
  };

  return (
    <div className="min-h-screen bg-white" dir={rtl ? "rtl" : "ltr"}>
      {/* ─── SEO HEAD ─────────────────────────── */}
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        {metaKeywords && <meta name="keywords" content={metaKeywords} />}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={canonicalUrl} />
        {primaryImage && <meta property="og:image" content={primaryImage} />}
        <meta property="og:locale" content={lang === "tr" ? "tr_TR" : lang === "en" ? "en_GB" : lang === "ar" ? "ar_SA" : "ru_RU"} />
        <meta property="og:site_name" content="OlyInvestment" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        {primaryImage && <meta name="twitter:image" content={primaryImage} />}

        {/* Hreflang */}
        {["tr", "en", "ar", "ru"].map((l) => (
          <link key={l} rel="alternate" hrefLang={l} href={`${BASE_URL}/${l}/properties/${property.slug || property.id}`} />
        ))}
        <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}/en/properties/${property.slug || property.id}`} />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(generateStructuredData(property, title, desc, location, lang, primaryImage))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateBreadcrumbSchema(lang, title))}
        </script>
      </Helmet>

      <Header />

      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb items={[
              { label: t("breadcrumb.properties"), href: `/${lang}#listings` },
              { label: title }
            ]} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left - Gallery + Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Image Gallery */}
              <div data-testid="property-gallery" className="relative rounded-sm overflow-hidden bg-[#F8F9FA]">
                <img
                  src={property.images[currentImg] || property.images[0]}
                  alt={`${title} - ${location}`}
                  className="w-full h-[300px] md:h-[480px] object-cover"
                  loading="eager"
                />
                <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                  <Badge className="bg-[#0A192F] text-white text-[10px] uppercase tracking-wider border-0 rounded-sm px-3 py-1">
                    {property.status === "satilik" ? t("property_detail.for_sale") : t("property_detail.for_rent")}
                  </Badge>
                  {property.featured && (
                    <Badge className="bg-[#D4AF37] text-white text-[10px] uppercase tracking-wider border-0 rounded-sm px-3 py-1">
                      {t("property_detail.featured")}
                    </Badge>
                  )}
                  {property.citizenshipEligible && (
                    <Badge className="bg-green-600 text-white text-[10px] uppercase tracking-wider border-0 rounded-sm px-3 py-1">
                      Citizenship Eligible
                    </Badge>
                  )}
                  {property.rentalGuaranteed && (
                    <Badge className="bg-blue-600 text-white text-[10px] uppercase tracking-wider border-0 rounded-sm px-3 py-1">
                      Guaranteed Rental
                    </Badge>
                  )}
                </div>
                <button data-testid="gallery-fav" className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white hover:scale-110 transition-all">
                  <Heart className="w-5 h-5 text-[#0A192F]" />
                </button>
                {property.images.length > 1 && (
                  <>
                    <button data-testid="gallery-prev" onClick={() => setCurrentImg((p) => (p - 1 + property.images.length) % property.images.length)} className={`absolute top-1/2 -translate-y-1/2 ${rtl ? "right-3" : "left-3"} w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-all`}>
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button data-testid="gallery-next" onClick={() => setCurrentImg((p) => (p + 1) % property.images.length)} className={`absolute top-1/2 -translate-y-1/2 ${rtl ? "left-3" : "right-3"} w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-all`}>
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {property.images.map((_, i) => (
                    <button key={i} onClick={() => setCurrentImg(i)} className={`w-2.5 h-2.5 rounded-full transition-all ${currentImg === i ? "bg-[#D4AF37] scale-125" : "bg-white/60"}`} />
                  ))}
                </div>
              </div>

              {/* Thumbnails */}
              {property.images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {property.images.map((img, i) => (
                    <button key={i} onClick={() => setCurrentImg(i)} className={`shrink-0 w-20 h-16 rounded-sm overflow-hidden border-2 transition-all ${currentImg === i ? "border-[#D4AF37]" : "border-transparent opacity-60 hover:opacity-100"}`}>
                      <img src={img} alt={`${title} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    </button>
                  ))}
                </div>
              )}

              {/* Title + Price */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-sm text-[#4A5568]" style={{ fontFamily: "'Outfit', sans-serif" }}>{location}</span>
                </div>
                <h1 data-testid="property-title" className="text-2xl md:text-3xl font-bold text-[#0A192F] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {title}
                </h1>
                <p className="text-2xl md:text-3xl font-bold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {formatPrice(property.priceGBP, i18n.language)}{property.rental ? t("property_detail.per_month") : ""}
                </p>
                {property.sqm > 0 && property.priceGBP > 0 && (
                  <p className="text-sm text-[#4A5568] mt-1">
                    {formatPrice(Math.round(property.priceGBP / property.sqm), i18n.language)} / m²
                  </p>
                )}
              </div>

              {/* ─── Yatırım Bilgileri ─────────────────── */}
              {(property.expectedROI || property.rentalYield || property.rentalIncomeMonthly || property.financingAvailable) && (
                <div>
                  <h2 className="text-lg font-semibold text-[#0A192F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    <TrendingUp className="w-5 h-5 text-[#D4AF37] inline-block mr-2" />
                    {lang === "tr" ? "Yatırım Bilgileri" : "Investment Details"}
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {property.expectedROI && (
                      <div className="p-4 bg-gradient-to-br from-[#D4AF37]/5 to-[#D4AF37]/10 rounded-sm border border-[#D4AF37]/20">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium">
                          {lang === "tr" ? "Beklenen ROI" : "Expected ROI"}
                        </p>
                        <p className="text-xl font-bold text-[#D4AF37]">{property.expectedROI}%</p>
                      </div>
                    )}
                    {property.rentalYield && (
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-sm border border-blue-200">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium">
                          {lang === "tr" ? "Kira Getirisi" : "Rental Yield"}
                        </p>
                        <p className="text-xl font-bold text-blue-700">{property.rentalYield}%</p>
                      </div>
                    )}
                    {property.rentalIncomeMonthly && (
                      <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-sm border border-green-200">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium">
                          {lang === "tr" ? "Aylık Kira" : "Monthly Rent"}
                        </p>
                        <p className="text-xl font-bold text-green-700">
                          {formatPrice(property.rentalIncomeMonthly, i18n.language)}
                        </p>
                      </div>
                    )}
                    {property.capitalAppreciationPotential && (
                      <div className="p-4 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium">
                          {lang === "tr" ? "Değer Artış Potansiyeli" : "Appreciation Potential"}
                        </p>
                        <p className="text-sm font-bold text-[#0A192F]">
                          {appreciationMap[property.capitalAppreciationPotential]?.[lang] || property.capitalAppreciationPotential}
                        </p>
                      </div>
                    )}
                    {property.rentalGuaranteed && property.rentalGuaranteeYears && (
                      <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-sm border border-purple-200">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium">
                          {lang === "tr" ? "Garantili Kira" : "Guaranteed Rental"}
                        </p>
                        <p className="text-xl font-bold text-purple-700">
                          {property.rentalGuaranteeYears} {lang === "tr" ? "Yıl" : "Years"}
                        </p>
                      </div>
                    )}
                    {property.financingAvailable && (
                      <div className="p-4 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium">
                          {lang === "tr" ? "Finansman" : "Financing"}
                        </p>
                        <p className="text-sm font-bold text-[#0A192F]">
                          {property.downPaymentPercentage ? `%${property.downPaymentPercentage} ` : ""}
                          {property.installmentMonths ? `${property.installmentMonths} Ay Taksit` : lang === "tr" ? "Mevcut" : "Available"}
                        </p>
                      </div>
                    )}
                  </div>
                  {property.paymentPlan && (
                    <div className="mt-4 p-4 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                      <p className="text-sm font-semibold text-[#0A192F] mb-1">
                        {lang === "tr" ? "Ödeme Planı:" : "Payment Plan:"}
                      </p>
                      <p className="text-sm text-[#4A5568]">{property.paymentPlan}</p>
                    </div>
                  )}
                </div>
              )}

              {/* ─── Proje Bilgileri ─────────────────── */}
              {(property.projectStatus || property.projectName || property.developerName) && (
                <div>
                  <h2 className="text-lg font-semibold text-[#0A192F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    <Building2 className="w-5 h-5 text-[#D4AF37] inline-block mr-2" />
                    {lang === "tr" ? "Proje Bilgileri" : "Project Information"}
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {property.projectName && (
                      <div className="p-4 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium">
                          {lang === "tr" ? "Proje" : "Project"}
                        </p>
                        <p className="text-sm font-semibold text-[#0A192F]">{property.projectName}</p>
                      </div>
                    )}
                    {property.developerName && (
                      <div className="p-4 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium">
                          {lang === "tr" ? "Geliştirici" : "Developer"}
                        </p>
                        <p className="text-sm font-semibold text-[#0A192F]">{property.developerName}</p>
                      </div>
                    )}
                    {property.projectStatus && (
                      <div className="p-4 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium">
                          {lang === "tr" ? "Durum" : "Status"}
                        </p>
                        <p className="text-sm font-semibold text-[#0A192F]">
                          {projectStatusMap[property.projectStatus]?.[lang] || property.projectStatus}
                        </p>
                      </div>
                    )}
                    {property.completionDate && (
                      <div className="p-4 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium">
                          {lang === "tr" ? "Teslim Tarihi" : "Completion"}
                        </p>
                        <p className="text-sm font-semibold text-[#0A192F]">
                          {new Date(property.completionDate).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-GB")}
                        </p>
                      </div>
                    )}
                    {property.totalUnits && (
                      <div className="p-4 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium">
                          {lang === "tr" ? "Toplam Ünite" : "Total Units"}
                        </p>
                        <p className="text-sm font-semibold text-[#0A192F]">{property.totalUnits}</p>
                      </div>
                    )}
                    {property.availableUnits && (
                      <div className="p-4 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                        <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium">
                          {lang === "tr" ? "Mevcut Ünite" : "Available"}
                        </p>
                        <p className="text-sm font-semibold text-[#0A192F]">{property.availableUnits}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ─── Vatandaşlık Bilgisi ─────────────── */}
              {property.citizenshipEligible && (
                <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-sm border border-green-200">
                  <div className="flex items-center gap-3 mb-3">
                    <Award className="w-6 h-6 text-green-600" />
                    <h3 className="text-lg font-semibold text-green-800">
                      {lang === "tr" ? "Vatandaşlık İçin Uygun" : "Citizenship Eligible"}
                    </h3>
                  </div>
                  <p className="text-sm text-green-700">
                    {lang === "tr"
                      ? "Bu gayrimenkul, KKTC vatandaşlığı almak için yatırım eşiğini karşılamaktadır."
                      : "This property meets the investment threshold for North Cyprus citizenship."
                    }
                  </p>
                  {property.citizenshipInvestmentThreshold && (
                    <p className="text-sm text-green-700 mt-1 font-semibold">
                      {lang === "tr" ? "Minimum Yatırım:" : "Min. Investment:"} {formatPrice(property.citizenshipInvestmentThreshold, i18n.language)}
                    </p>
                  )}
                  {property.legalStatus && (
                    <p className="text-sm text-green-700 mt-1">
                      {lang === "tr" ? "Yasal Durum:" : "Legal Status:"} {property.legalStatus}
                    </p>
                  )}
                </div>
              )}

              {/* Features Grid */}
              <div>
                <h2 className="text-lg font-semibold text-[#0A192F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t("property_detail.features")}
                </h2>
                <div data-testid="features-grid" className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {features.map((f, i) => {
                    const Icon = f.icon;
                    return (
                      <div key={i} className="flex items-center gap-3 p-4 bg-[#F8F9FA] rounded-sm border border-[#E2E8F0]">
                        <Icon className="w-5 h-5 text-[#D4AF37] shrink-0" />
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-[#4A5568] font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>{f.label}</p>
                          <p className="text-sm font-semibold text-[#0A192F]" style={{ fontFamily: "'Outfit', sans-serif" }}>{f.value}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Ek Olanaklar */}
              {amenities.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-[#0A192F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {lang === "tr" ? "Ek Olanaklar" : "Amenities"}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {amenities.map((a, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#F8F9FA] border border-[#E2E8F0] rounded-full text-xs text-[#0A192F]">
                        {a.icon} {a.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              <div>
                <h2 className="text-lg font-semibold text-[#0A192F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {t("property_detail.description")}
                </h2>
                <div className="text-[#4A5568] leading-relaxed whitespace-pre-line" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {desc}
                </div>
              </div>

              {/* Video */}
              {property.videoUrl && (
                <div>
                  <h2 className="text-lg font-semibold text-[#0A192F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    <Video className="w-5 h-5 text-[#D4AF37] inline-block mr-2" />
                    {lang === "tr" ? "Video Tur" : "Video Tour"}
                  </h2>
                  <a
                    href={property.videoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#D4AF37] hover:underline"
                  >
                    <Play className="w-4 h-4" />
                    {lang === "tr" ? "Videoyu İzle" : "Watch Video"}
                  </a>
                </div>
              )}

              {/* Floor Plan & Brochure */}
              {(property.floorPlanUrl || property.brochureUrl || property.virtualTourUrl) && (
                <div className="flex flex-wrap gap-3">
                  {property.floorPlanUrl && (
                    <a
                      href={property.floorPlanUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-[#E2E8F0] rounded-sm text-sm hover:bg-[#F8F9FA] transition-colors"
                    >
                      <FileText className="w-4 h-4 text-[#D4AF37]" />
                      {lang === "tr" ? "Kat Planı" : "Floor Plan"}
                    </a>
                  )}
                  {property.brochureUrl && (
                    <a
                      href={property.brochureUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-[#E2E8F0] rounded-sm text-sm hover:bg-[#F8F9FA] transition-colors"
                    >
                      <Download className="w-4 h-4 text-[#D4AF37]" />
                      {lang === "tr" ? "Broşür İndir" : "Download Brochure"}
                    </a>
                  )}
                  {property.virtualTourUrl && (
                    <a
                      href={property.virtualTourUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-[#E2E8F0] rounded-sm text-sm hover:bg-[#F8F9FA] transition-colors"
                    >
                      <RotateCcw className="w-4 h-4 text-[#D4AF37]" />
                      {lang === "tr" ? "Sanal Tur" : "Virtual Tour"}
                    </a>
                  )}
                </div>
              )}

              {/* Map */}
              {property.lat && property.lng && (
                <div>
                  <h2 className="text-lg font-semibold text-[#0A192F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {t("property_detail.location_map")}
                  </h2>
                  <div data-testid="property-map" className="h-[300px] bg-[#F8F9FA] rounded-sm border border-[#E2E8F0] flex items-center justify-center">
                    <iframe
                      title="map"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d50000!2d${property.lng}!3d${property.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2s`}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar - Contact Form */}
            <div className="space-y-6">
              <div className="sticky top-24">
                {/* Investment Highlight */}
                {(property.expectedROI || property.rentalYield) && (
                  <div className="bg-gradient-to-br from-[#0A192F] to-[#1a2f4f] text-white rounded-sm p-5 mb-6">
                    <h3 className="text-sm font-semibold text-[#D4AF37] uppercase tracking-wider mb-3">
                      {lang === "tr" ? "Yatırım Özeti" : "Investment Summary"}
                    </h3>
                    {property.expectedROI && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-300">{lang === "tr" ? "ROI" : "ROI"}</span>
                        <span className="text-lg font-bold text-[#D4AF37]">{property.expectedROI}%</span>
                      </div>
                    )}
                    {property.rentalYield && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-300">{lang === "tr" ? "Kira Getirisi" : "Rental Yield"}</span>
                        <span className="text-lg font-bold text-white">{property.rentalYield}%</span>
                      </div>
                    )}
                    {property.rentalIncomeMonthly && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-300">{lang === "tr" ? "Aylık Kira" : "Monthly"}</span>
                        <span className="text-lg font-bold text-white">{formatPrice(property.rentalIncomeMonthly, i18n.language)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Contact Form Card */}
                <div data-testid="contact-form-card" className="bg-white border border-[#E2E8F0] rounded-sm p-6 shadow-lg mb-6">
                  <h3 className="text-lg font-semibold text-[#0A192F] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {t("property_detail.request_info")}
                  </h3>
                  {formSent ? (
                    <div className="text-center py-8">
                      <p className="text-[#25D366] font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {t("property_detail.form_success")}
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-3">
                      <input data-testid="form-name" type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder={t("property_detail.form_name")} required className="w-full px-4 py-3 bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm text-sm focus:outline-none focus:border-[#D4AF37] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }} />
                      <input data-testid="form-email" type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder={t("property_detail.form_email")} required className="w-full px-4 py-3 bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm text-sm focus:outline-none focus:border-[#D4AF37] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }} />
                      <input data-testid="form-phone" type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder={t("property_detail.form_phone")} className="w-full px-4 py-3 bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm text-sm focus:outline-none focus:border-[#D4AF37] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }} />
                      <textarea data-testid="form-message" name="message" value={formData.message} onChange={handleInputChange} placeholder={t("property_detail.form_message")} rows={3} className="w-full px-4 py-3 bg-[#F8F9FA] border border-[#E2E8F0] rounded-sm text-sm focus:outline-none focus:border-[#D4AF37] transition-colors resize-none" style={{ fontFamily: "'Outfit', sans-serif" }} />
                      <button data-testid="form-submit" type="submit" disabled={formLoading} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#D4AF37] text-white font-medium rounded-sm hover:bg-[#B5952F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        <Send className="w-4 h-4" />{formLoading ? t("property_detail.sending") || "Sending..." : t("property_detail.form_send")}
                      </button>
                    </form>
                  )}
                </div>

                {/* Quick CTA */}
                <div className="space-y-3">
                  <a href="https://wa.me/905338000000" target="_blank" rel="noopener noreferrer" data-testid="detail-whatsapp-btn" className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#25D366] text-white font-medium rounded-sm hover:bg-[#128C7E] transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.126.553 4.121 1.521 5.856L0 24l6.335-1.652A11.95 11.95 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.75c-1.875 0-3.65-.516-5.178-1.41l-.372-.22-3.86 1.01 1.038-3.785-.242-.385A9.693 9.693 0 012.25 12c0-5.385 4.365-9.75 9.75-9.75S21.75 6.615 21.75 12s-4.365 9.75-9.75 9.75z" />
                    </svg>
                    {t("property_detail.whatsapp_contact")}
                  </a>
                  <a href="tel:+905338000000" data-testid="detail-call-btn" className="w-full flex items-center justify-center gap-2 px-6 py-3 border-2 border-[#0A192F] text-[#0A192F] font-medium rounded-sm hover:bg-[#0A192F] hover:text-white transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    <Phone className="w-4 h-4" />{t("property_detail.call_now")}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Similar Listings */}
          {similar.length > 0 && (
            <div className="mt-20">
              <h2 className="text-2xl font-bold text-[#0A192F] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t("property_detail.similar")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {similar.map((sp) => (
                  <Link key={sp.id} to={`/${lang}/properties/${sp.id}`} data-testid={`similar-${sp.id}`} className="group bg-white border border-[#E2E8F0] rounded-sm overflow-hidden hover:shadow-xl transition-all duration-500">
                    <div className="relative h-48 overflow-hidden">
                      <img src={sp.images[0]} alt={t(`listings.properties.${sp.titleKey}.title`)} className="w-full h-full object-cover card-img-zoom" loading="lazy" />
                    </div>
                    <div className="p-5">
                      <p className="text-xs text-[#4A5568] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{t(`listings.properties.${sp.titleKey}.location`)}</p>
                      <h3 className="text-base font-semibold text-[#0A192F] mb-2 group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>{t(`listings.properties.${sp.titleKey}.title`)}</h3>
                      <p className="text-lg font-bold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {formatPrice(sp.priceGBP, i18n.language)}{sp.rental ? t("property_detail.per_month") : ""}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
